import { Atom, atom, Getter, PrimitiveAtom, Setter } from 'jotai';
import { WritableAtom } from 'jotai/core/atom';

import { isSuccess, map, Remote } from 'utils/remote';

import { DataAtom } from './dataAtom';

export type DataFetchingAtom<Payload> = Atom<Remote<Payload>>;
export type WritableDataFetchingAtom<Payload, Variables> = WritableAtom<
  Remote<Payload>,
  Variables,
  Promise<void>
>;

export function getFormStateAtom() {
  return atom<FormState>({ status: 'INITIAL' });
}

function getErrorMessage(error: unknown) {
  console.error(error);
  return error instanceof Error
    ? error.message
    : `Something went wrong: ${JSON.stringify(error)}`;
}

export function setFetchingErrorStatus<Payload>(
  set: Setter,
  dataAtom: PrimitiveAtom<Remote<Payload>>,
  error: unknown,
) {
  set(dataAtom, {
    fetchStatus: 'ERROR',
    errorMessage: getErrorMessage(error),
  });
}

const fetchData = async <Payload, Variables>(
  dataAtom: PrimitiveAtom<Remote<Payload>>,
  fetcher: (variables: Variables, get: Getter, set: Setter) => Promise<Payload>,
  get: Getter,
  set: Setter,
  variables: Variables,
): Promise<void> => {
  set(dataAtom, { fetchStatus: 'LOADING' });
  const data = await fetcher(variables, get, set);

  set(dataAtom, { fetchStatus: 'SUCCESS', data });
};

/**
 * Use this method in case you don't need to differentiate between initial loading and refreshing
 * data fetching states.
 *
 * Payload: the dataAtom's payload signature.
 * Variables: object of query variables required for the fetch function.
 * @param dataAtom - see getDataAtom above.
 * @param fetcher - async function that returns the payload.
 * @returns an atom that can be used with jotai useAtom, useAtomValue or useSetAtom,
 *          to return the atom's current state and a fetch / re-fetch function.
 */
export function getDataFetchingAtom<Payload, Variables>(
  dataAtom: PrimitiveAtom<Remote<Payload>>,
  fetcher: (variables: Variables, get: Getter, set: Setter) => Promise<Payload>,
): WritableDataFetchingAtom<Payload, Variables> {
  return atom(
    (get) => get(dataAtom),
    async (get, set, variables: Variables) => {
      try {
        await fetchData(dataAtom, fetcher, get, set, variables);
      } catch (error) {
        setFetchingErrorStatus(set, dataAtom, error);
      }
    },
  );
}

const fetchOrRefreshData = async <Payload, Variables>(
  dataAtom: PrimitiveAtom<Remote<Payload>>,
  fetcher: (variables: Variables, get: Getter, set: Setter) => Promise<Payload>,
  get: Getter,
  set: Setter,
  variables: Variables,
): Promise<void> => {
  set(dataAtom, (remote) => {
    if (isSuccess(remote)) {
      return { data: remote.data, fetchStatus: 'REFRESHING' };
    }

    return { fetchStatus: 'LOADING' };
  });

  const data = await fetcher(variables, get, set);
  set(dataAtom, { fetchStatus: 'SUCCESS', data });
};

/**
 * Use this method in case you want to differentiate between initial loading and refreshing
 * data fetching states.
 *
 * Payload: the dataAtom's payload signature.
 * Variables: object of query variables required for the fetch function.
 * @param dataAtom - see getDataAtom above.
 * @param fetcher - async function that returns the payload.
 * @returns an atom that can be used with jotai useAtom, useAtomValue or useSetAtom,
 *          to return the atom's current state and a fetch / re-fetch function.
 */
export function getDataRefreshingAtom<Payload, Variables>(
  dataAtom: PrimitiveAtom<Remote<Payload>>,
  fetcher: (variables: Variables, get: Getter, set: Setter) => Promise<Payload>,
): WritableDataFetchingAtom<Payload, Variables> {
  return atom(
    (get) => get(dataAtom),
    async (get, set, variables: Variables) => {
      try {
        await fetchOrRefreshData(dataAtom, fetcher, get, set, variables);
      } catch (error) {
        setFetchingErrorStatus(set, dataAtom, error);
      }
    },
  );
}

export type FormState = {
  status: 'INITIAL' | 'LOADING' | 'SUCCESS' | 'ERROR';
  errorMessage?: string;
};

export type WritableFormUpdatingAtom<Variables> = WritableAtom<
  FormState,
  Variables,
  Promise<void>
>;

export const isFormInitialised = ({ status }: FormState) =>
  status !== 'INITIAL';

export const isFormLoading = ({ status }: FormState) => status === 'LOADING';

export const isFormSuccess = ({ status }: FormState) => status === 'SUCCESS';

export const isFormReady = ({ status }: FormState) =>
  status === 'SUCCESS' || status === 'ERROR';

export const isFormFaulty = ({ status }: FormState) => status === 'ERROR';

/**
 *
 */
export function getFormUpdatingAtom<Payload, Variables>(
  fetcher: (variables: Variables, get: Getter, set: Setter) => Promise<Payload>,
  dataAtom?: PrimitiveAtom<Remote<Payload>>,
  formStateAtom: PrimitiveAtom<FormState> = atom<FormState>({
    status: 'INITIAL',
  }),
): WritableFormUpdatingAtom<Variables> {
  return atom(
    (get) => get(formStateAtom),
    async (get, set, variables: Variables) => {
      set(formStateAtom, { status: 'LOADING' });
      try {
        const data = await fetcher(variables, get, set);

        if (dataAtom) {
          set(dataAtom, {
            fetchStatus: 'SUCCESS',
            data,
          });
        }

        set(formStateAtom, { status: 'SUCCESS' });
        setTimeout(() => {
          set(formStateAtom, { status: 'INITIAL' });
        }, 0);
      } catch (error) {
        set(formStateAtom, {
          status: 'ERROR',
          errorMessage: getErrorMessage(error),
        });
      }
    },
  );
}

type UdpateMessage<A, R> =
  | {
      action: 'REMOVE';
      item: R;
    }
  | {
      action: 'ADD';
      item: A;
    };

export const getSubscriptionUpdatingAtom = <
  A extends { id: string },
  R extends { id: string },
>(
  dataAtom: DataAtom<A[]>,
  subscriber: (
    handler: (message: UdpateMessage<A, R>) => void,
  ) => Promise<() => void>,
  onUpdate?: (message: UdpateMessage<A, R>, get: Getter, set: Setter) => void,
) => {
  return getSubscriptionAtom(subscriber, (message, get, set) => {
    if (message.action === 'ADD') {
      set(dataAtom, (remote) =>
        map(remote, (items) => [message.item, ...items]),
      );
    }
    if (message.action === 'REMOVE') {
      set(dataAtom, (remote) =>
        map(remote, (items) =>
          items.filter(({ id }) => id !== message.item.id),
        ),
      );
    }
    onUpdate?.(message, get, set);
  });
};

export const getSubscriptionAtom = <T>(
  subscriber: (handler: (message: T) => void) => Promise<() => void>,
  onUpdate?: (message: T, get: Getter, set: Setter) => void,
) => {
  const unsubAtom = atom<(() => void) | null>(null);
  return atom(
    (get) => get(unsubAtom),
    async (get, set) => {
      const unsubscribe = await subscriber((message) => {
        onUpdate?.(message, get, set);
      });
      set(unsubAtom, () => unsubscribe);
    },
  );
};
